import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { IMAGES, EARNINGS, PARTNERS } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

const GALLERY = [
  { src: IMAGES.placement1, alt: "ABHI students during bakery practicals in the training kitchen", offset: "" },
  { src: IMAGES.placement2, alt: "F&B service training session at ABHI", offset: "sm:mt-14" },
  { src: IMAGES.placement3, alt: "Culinary students cooking in the campus kitchen", offset: "sm:mt-7" },
];

export default function Placements({ scrollTo }) {
  return (
    <section
      id="placements"
      data-testid="placements-section"
      className="bg-brand-green py-28 text-white md:py-36"
    >
      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:gap-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="lg:col-span-5"
        >
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#E8C4B0]">
            Placements · Global Exposure
          </p>
          <h2 className="mt-5 font-serif text-4xl font-light leading-tight tracking-tight md:text-5xl">
            From Dwarka to the world's <em className="italic text-[#E8C4B0]">finest hotels</em>.
          </h2>
          <p className="mt-8 max-w-md text-base leading-relaxed text-white/70 md:text-lg">
            We offer unmatched global exposure through international internships
            and placements in top hotels and resorts around the world — from
            Parisian kitchens to Maldivian resorts.
          </p>
          <div className="mt-10 space-y-4 border-t border-white/15 pt-8">
            {[
              ["100+", "International placements"],
              ["5", "Countries and counting"],
              ["100%", "Batch placement record"],
            ].map(([value, label]) => (
              <div key={label} className="flex items-baseline gap-5">
                <span className="w-20 font-serif text-3xl text-[#E8C4B0]">{value}</span>
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60">
                  {label}
                </span>
              </div>
            ))}
          </div>
          <div
            data-testid="job-assurance-highlight"
            className="mt-8 flex items-start gap-4 rounded-md border border-[#E8C4B0]/40 bg-white/5 p-5"
          >
            <ShieldCheck className="h-7 w-7 shrink-0 text-[#E8C4B0]" />
            <p className="text-sm leading-relaxed text-white/90 md:text-base">
              <span className="font-serif text-lg italic text-[#E8C4B0]">Job Assurance. </span>
              Every ABHI student is assured a job in the top hotels of India and
              abroad — backed by our 100% placement record since 2008.
            </p>
          </div>
          <button
            data-testid="placements-enquire-button"
            onClick={() => scrollTo("#enquire")}
            className="group mt-12 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-brand-green transition-colors duration-300 hover:bg-[#E8C4B0] hover:text-brand-ink"
          >
            Start Your Application
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:col-span-7">
          {GALLERY.map((img, i) => (
            <motion.figure
              key={img.src}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: EASE, delay: i * 0.12 }}
              className={`overflow-hidden rounded-md ${img.offset} ${i === 2 ? "max-sm:col-span-2" : ""}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="aspect-[3/4] w-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </motion.figure>
          ))}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
        data-testid="placement-partners"
        className="mx-auto mt-20 max-w-7xl border-t border-white/15 px-6 pt-12"
      >
        <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#E8C4B0]">
          Training & Placement Partners · India
        </p>
        <div className="mt-8 flex flex-wrap items-baseline gap-x-10 gap-y-5">
          {PARTNERS.map((partner, i) => (
            <span key={partner} className="flex items-baseline gap-10">
              <span
                data-testid={`partner-${i}`}
                className={`font-serif text-2xl transition-colors duration-300 hover:text-white md:text-3xl ${
                  i % 2 === 1 ? "italic text-[#E8C4B0]/80" : "text-white/75"
                }`}
              >
                {partner}
              </span>
              {i < PARTNERS.length - 1 && (
                <span aria-hidden="true" className="text-sm text-[#E8C4B0]/50">
                  ✦
                </span>
              )}
            </span>
          ))}
          <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/50">
            & many more
          </span>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
        data-testid="internship-earnings"
        className="mx-auto mt-20 max-w-7xl px-6"
      >
        <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#E8C4B0]">
          What Our Interns Earn Abroad · Per Month
        </p>
        <div className="mt-8 grid gap-px overflow-hidden rounded-md border border-white/15 bg-white/15 sm:grid-cols-3">
          {EARNINGS.map((e) => (
            <div
              key={e.country}
              data-testid={`earning-${e.country.toLowerCase().replace(/\s/g, "-")}`}
              className="bg-brand-green p-8 transition-colors duration-300 hover:bg-[#33584d]"
            >
              <p className="font-serif text-4xl text-white md:text-5xl">{e.amount}</p>
              <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-white/60">
                approx. per month
              </p>
              <p className="mt-5 font-serif text-xl italic text-[#E8C4B0]">{e.country}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-white/50">
          Indicative monthly stipends earned by ABHI students during international internships.
        </p>
      </motion.div>
    </section>
  );
}
